import { z } from 'zod';

import type { ProjectPermission } from './authorization.js';

/**
 * TEAM CONVERSATIONS (ADR 0021). The rules here are the spec's, sections 2–4:
 * `docs/superpowers/specs/2026-10-28-workspace-conversations-design.md`.
 *
 * The API, the digest sweep and the SPA's hints all read these predicates, so
 * "may read", "may be invited" and "may see the source object" are answered the
 * same way everywhere — and they are THREE questions, not one. Folding them
 * together is what made the first draft contradict itself: a check that refuses
 * anybody not yet in a private thread cannot also be the check for inviting
 * somebody into it.
 */

/** What a thread is about. PLACE is a `location` row (listing or POI) that is a
 *  candidate in the project, keyed by the location id alone, so a thread survives
 *  the pin being promoted to a listing. */
export const ConversationAnchorType = z.enum(['PROJECT', 'SCENE', 'REQUIREMENT', 'PLACE', 'ELEMENT', 'REFERENCE']);
export type ConversationAnchorType = z.infer<typeof ConversationAnchorType>;

/** Fixed at creation in V1. */
export const ConversationVisibility = z.enum(['PUBLIC', 'PRIVATE']);
export type ConversationVisibility = z.infer<typeof ConversationVisibility>;

export const ConversationStatus = z.enum(['OPEN', 'RESOLVED']);
export type ConversationStatus = z.infer<typeof ConversationStatus>;

/** Subscription, which is NOT visibility: reading a public thread does not follow it. */
export const ConversationFollowState = z.enum(['FOLLOWING', 'NOT_FOLLOWING', 'MUTED']);
export type ConversationFollowState = z.infer<typeof ConversationFollowState>;

export const ConversationFollowReason = z.enum(['CREATED', 'POSTED', 'ADDED', 'MENTIONED', 'MANUAL']);
export type ConversationFollowReason = z.infer<typeof ConversationFollowReason>;

/** Private threads only. Removal and leaving are states — history and the record
 *  that somebody was there stay (ADR 0011). */
export const ConversationParticipantState = z.enum(['ACTIVE', 'REMOVED', 'LEFT']);
export type ConversationParticipantState = z.infer<typeof ConversationParticipantState>;

export const ConversationParticipantRole = z.enum(['OWNER', 'MEMBER']);
export type ConversationParticipantRole = z.infer<typeof ConversationParticipantRole>;

/** Only MESSAGE counts as unread; the rest are system lines recording a change. */
export const ConversationMessageKind = z.enum([
  'MESSAGE',
  'PARTICIPANT_ADDED',
  'PARTICIPANT_REMOVED',
  'PARTICIPANT_LEFT',
  'OWNER_CHANGED',
  'RESOLVED',
  'REOPENED',
  'RENAMED',
]);
export type ConversationMessageKind = z.infer<typeof ConversationMessageKind>;

/** AUTHOR withdrew it; PROJECT is a member holding CONVERSATION_MODERATE; STAFF is ScoutAround. */
export const ConversationHiddenBy = z.enum(['AUTHOR', 'PROJECT', 'STAFF']);
export type ConversationHiddenBy = z.infer<typeof ConversationHiddenBy>;

/**
 * Computed on read, never stored. LIVE — the anchor row is live (a DISMISSED
 * requirement is still live). REMOVED — soft-deleted or superseded, or a place
 * with no live candidate left; the thread reads from its snapshot. EARLIER_DRAFT —
 * a scene of another script version, or one cleared by a parse retry.
 */
export const ConversationAnchorState = z.enum(['LIVE', 'REMOVED', 'EARLIER_DRAFT']);
export type ConversationAnchorState = z.infer<typeof ConversationAnchorState>;

/** Per project member: the email digest, or none. Never changes what they can read. */
export const ConversationEmailPreference = z.enum(['DIGEST', 'OFF']);
export type ConversationEmailPreference = z.infer<typeof ConversationEmailPreference>;

export const ConversationReportReason = z.enum(['HARASSMENT', 'SPAM', 'CONFIDENTIAL', 'OTHER']);
export type ConversationReportReason = z.infer<typeof ConversationReportReason>;

/** Why a ScoutAround administrator opened a conversation. Required on every content read. */
export const ConversationStaffReason = z.enum(['REPORT_REVIEW', 'SUPPORT_REQUEST', 'LEGAL', 'OTHER']);
export type ConversationStaffReason = z.infer<typeof ConversationStaffReason>;

export const CONVERSATION_TITLE_MAX = 160;
export const CONVERSATION_MESSAGE_MAX = 5000;
/** A private thread is created with its people in one request; more are added later. */
export const CONVERSATION_PARTICIPANTS_MAX = 50;

/**
 * The anchor's own feature read. PROJECT needs none beyond reading conversations;
 * a scene or an element is part of the script, a requirement of the breakdown, a
 * place of the location search, a reference of the References library.
 */
export const ANCHOR_READ_PERMISSION: Readonly<
  Record<ConversationAnchorType, ProjectPermission | null>
> = Object.freeze({
  PROJECT: null,
  SCENE: 'READ_SCRIPT',
  ELEMENT: 'READ_SCRIPT',
  REQUIREMENT: 'READ_REQUIREMENTS',
  PLACE: 'READ_LOCATIONS',
  // A reference library item (2026-11-02): read through its own feature.
  REFERENCE: 'READ_REFERENCES',
});

/** The project member as the predicates need it: their status and effective permissions. */
export interface ConversationActor {
  /** `script_breakdown_project_member.status`. Anything but ACTIVE fails every check. */
  readonly memberStatus: string;
  readonly permissions: readonly ProjectPermission[];
}

export interface ConversationThreadAccess {
  readonly anchorType: ConversationAnchorType;
  readonly visibility: ConversationVisibility;
  /** Set when ScoutAround staff hid the whole thread. */
  readonly staffHidden: boolean;
}

/** May this person see the thread's SOURCE OBJECT's feature at all? */
export function canReadAnchor(
  permissions: readonly ProjectPermission[],
  anchorType: ConversationAnchorType,
): boolean {
  const needed = ANCHOR_READ_PERMISSION[anchorType];
  return needed === null || permissions.includes(needed);
}

function isReaderOfConversations(actor: ConversationActor): boolean {
  return actor.memberStatus === 'ACTIVE' && actor.permissions.includes('READ_CONVERSATIONS');
}

/**
 * May they be INVITED, ADDED or (on a private thread) become mentionable?
 *
 * Deliberately does not require that they can read the thread NOW — on a private
 * thread nobody can until they are added, and that is the point of adding them.
 * Someone already ACTIVE in it is not "joinable"; adding them again is a no-op.
 */
export function canJoinThread(
  actor: ConversationActor,
  thread: ConversationThreadAccess,
  participantState: ConversationParticipantState | null,
): boolean {
  return (
    isReaderOfConversations(actor) &&
    canReadAnchor(actor.permissions, thread.anchorType) &&
    !thread.staffHidden &&
    participantState !== 'ACTIVE'
  );
}

/**
 * May they read it NOW? A public thread: any active member who reads
 * conversations and the anchor's feature. A private one: only its ACTIVE
 * participants. ScoutAround staff never come through here — their reads go
 * through the audited internal path.
 */
export function canReadThread(
  actor: ConversationActor,
  thread: ConversationThreadAccess,
  participantState: ConversationParticipantState | null,
): boolean {
  if (!isReaderOfConversations(actor)) return false;
  if (!canReadAnchor(actor.permissions, thread.anchorType)) return false;
  if (thread.staffHidden) return false;
  return thread.visibility === 'PUBLIC' || participantState === 'ACTIVE';
}

/**
 * A mention is stored as `<@123>` and rendered as the person's name by the
 * reader, so a renamed account reads correctly in old messages. Ids are returned
 * once each, in first-seen order.
 */
const MENTION_TOKEN = /<@(\d{1,10})>/g;

export function mentionedUserIds(body: string): number[] {
  const seen = new Set<number>();
  for (const match of body.matchAll(MENTION_TOKEN)) {
    const id = Number(match[1]);
    if (Number.isSafeInteger(id) && id > 0) seen.add(id);
  }
  return [...seen];
}

/**
 * A LOCATION IN A MESSAGE is stored as a token too: `<#place:123>` for a place
 * (a `location.id` that is a candidate in the production, as a PLACE anchor is)
 * and `<#requirement:01J…>` for a script location (any id of up to 26 letters
 * and digits — the column is `char(26)`, and seeded rows are not all ULIDs; the
 * server checks every one against the production). The body carries the id
 * only; every reader is shown the place as THEIR answer from the production's
 * locations read describes it — the public title while it is listed, a pin's
 * derived label, nothing at all without the feature's read permission — so the
 * text of a message never carries a name the projection would withhold.
 */
const BODY_TOKEN = /<@(\d{1,10})>|<#place:(\d{1,10})>|<#requirement:([0-9A-Za-z]{1,26})>/g;

/** The most locations one message may point at; each is checked against the production. */
export const CONVERSATION_LOCATION_REFERENCES_MAX = 20;

export function placeToken(locationId: number): string {
  return `<#place:${String(locationId)}>`;
}

export function requirementToken(requirementId: string): string {
  return `<#requirement:${requirementId}>`;
}

/** The places and script locations a body points at, each once, in first-seen order. */
export function referencedLocations(body: string): { placeIds: number[]; requirementIds: string[] } {
  const places = new Set<number>();
  const requirements = new Set<string>();
  for (const match of body.matchAll(BODY_TOKEN)) {
    if (match[2] !== undefined) {
      const id = Number(match[2]);
      if (Number.isSafeInteger(id) && id > 0) places.add(id);
    }
    if (match[3] !== undefined) requirements.add(match[3]);
  }
  return { placeIds: [...places], requirementIds: [...requirements] };
}

export type ConversationMessagePart =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; userId: number }
  | { kind: 'place'; locationId: number }
  | { kind: 'requirement'; requirementId: string };

/** Splits a body into text, mention and location parts for rendering. Never interprets HTML. */
export function messageParts(body: string): readonly ConversationMessagePart[] {
  const parts: ConversationMessagePart[] = [];
  let last = 0;
  for (const match of body.matchAll(BODY_TOKEN)) {
    const at = match.index;
    if (at > last) parts.push({ kind: 'text', text: body.slice(last, at) });
    if (match[1] !== undefined) parts.push({ kind: 'mention', userId: Number(match[1]) });
    else if (match[2] !== undefined) parts.push({ kind: 'place', locationId: Number(match[2]) });
    else parts.push({ kind: 'requirement', requirementId: match[3] ?? '' });
    last = at + match[0].length;
  }
  if (last < body.length) parts.push({ kind: 'text', text: body.slice(last) });
  return parts;
}

// ---- Wire contracts ---------------------------------------------------------

const MessageBody = z
  .string()
  .transform((s) => s.trim())
  .pipe(z.string().min(1).max(CONVERSATION_MESSAGE_MAX));

const Title = z
  .string()
  .transform((s) => s.trim())
  .pipe(z.string().min(1).max(CONVERSATION_TITLE_MAX));

export const CreateConversationInput = z.object({
  /** Makes create idempotent: a retried POST returns the thread it already made. */
  clientRequestId: z.uuid(),
  anchorType: ConversationAnchorType,
  anchorId: z.string().min(1).max(26),
  title: Title,
  visibility: ConversationVisibility,
  body: MessageBody,
  /** Private threads only: the people added at creation, besides the creator. */
  participantUserIds: z.array(z.number().int().positive()).max(CONVERSATION_PARTICIPANTS_MAX).default([]),
});
export type CreateConversationInput = z.infer<typeof CreateConversationInput>;

export const PostConversationMessageInput = z.object({
  clientMessageId: z.uuid(),
  body: MessageBody,
});
export type PostConversationMessageInput = z.infer<typeof PostConversationMessageInput>;

export const RenameConversationInput = z.object({ title: Title });

export const ConversationReadInput = z.object({ seq: z.number().int().min(0) });

export const ConversationFollowInput = z.object({ state: ConversationFollowState });

export const AddConversationParticipantsInput = z.object({
  userIds: z.array(z.number().int().positive()).min(1).max(CONVERSATION_PARTICIPANTS_MAX),
});

export const ConversationEmailInput = z.object({ email: ConversationEmailPreference });

export const ReportConversationMessageInput = z.object({
  reasonCode: ConversationReportReason,
  note: z.string().trim().max(1000).optional(),
});

export const HideConversationMessageInput = z.object({
  reason: z.string().trim().min(1).max(64),
});

export const ConversationListFilter = z.enum(['all', 'following', 'mentions', 'resolved', 'removed']);
export type ConversationListFilter = z.infer<typeof ConversationListFilter>;

/** The caller's own state in a thread, as every list item and thread read carries it. */
export interface ConversationMe {
  readonly followState: ConversationFollowState;
  readonly participant: ConversationParticipantState | null;
  readonly participantRole: ConversationParticipantRole | null;
  /** Unread MESSAGE rows — computed only for a FOLLOWING thread, else 0. */
  readonly unread: number;
  /** Something newer than the caller's cursor, for a readable thread they do not follow. */
  readonly hasNew: boolean;
  readonly mentioned: boolean;
  readonly readSeq: number;
}

export interface ConversationThreadView {
  readonly id: string;
  readonly anchorType: ConversationAnchorType;
  readonly anchorId: string;
  readonly anchorLabel: string;
  readonly anchorState: ConversationAnchorState;
  readonly anchorSnapshot: Readonly<Record<string, unknown>> | null;
  /** Null only when ScoutAround redacted it. */
  readonly title: string | null;
  readonly visibility: ConversationVisibility;
  readonly status: ConversationStatus;
  readonly createdByUserId: number;
  readonly createdAt: string;
  readonly lastMessageAt: string | null;
  readonly messageCount: number;
  readonly lastSeq: number;
  readonly participantUserIds: readonly number[];
  readonly me: ConversationMe;
}

export interface ConversationMessageView {
  readonly id: string;
  readonly seq: number;
  readonly kind: ConversationMessageKind;
  readonly authorUserId: number;
  /** Null when hidden or redacted — the reader shows a placeholder naming why. */
  readonly body: string | null;
  readonly hiddenBy: ConversationHiddenBy | null;
  readonly redacted: boolean;
  readonly systemPayload: Readonly<Record<string, unknown>> | null;
  readonly createdAt: string;
}

export interface ConversationAnchorSummary {
  readonly anchorType: ConversationAnchorType;
  readonly anchorId: string;
  readonly threads: number;
  readonly unread: number;
  readonly mentioned: boolean;
  readonly hasNew: boolean;
}

export interface ConversationSummary {
  readonly totals: { readonly unread: number; readonly mentions: number };
  readonly anchors: readonly ConversationAnchorSummary[];
}

/** What a place is to one script location of the production: a candidate in some state. */
export interface ConversationPlaceCandidacy {
  readonly requirementId: string;
  readonly requirementTitle: string;
  /** The candidate's own status: LONGLIST, SHORTLIST, REJECTED or ARCHIVED. */
  readonly status: string;
  /** Chosen as the script location's primary or backup place. */
  readonly selection: 'PRIMARY' | 'BACKUP' | null;
}

/**
 * A PLACE AS A CONVERSATION SHOWS IT — a thread's place anchor and every
 * `<#place:…>` in a message. Producer-safe by construction, like every other
 * workspace surface: a listing carries its public title, cover photograph and
 * rate only while the marketplace lists it; a pin (`POI`) carries its derived
 * label ("Warehouse · Sofia") and nothing else — no photograph, no position.
 */
export interface ConversationPlace {
  readonly locationId: number;
  readonly kind: 'LISTING' | 'POI';
  readonly title: string;
  readonly city: string | null;
  readonly countryIso: string | null;
  readonly typeCode: string | null;
  readonly rateAmount: number | null;
  readonly priceOnRequest: boolean;
  readonly imageUrl: string | null;
  /** Publicly listed now — the only case with a page on the ScoutAround site. */
  readonly listed: boolean;
  /** A live candidate of the production. False once every candidate row is removed. */
  readonly inProduction: boolean;
  /** Empty for a reader without `READ_REQUIREMENTS`. */
  readonly candidacies: readonly ConversationPlaceCandidacy[];
}

/** A script location (requirement) as a conversation shows it. */
export interface ConversationScriptLocation {
  readonly requirementId: string;
  readonly title: string;
  readonly status: string;
  /** Not superseded or archived — what the production works from. */
  readonly live: boolean;
  readonly sceneCount: number;
  /** Live candidates, and how many of them are shortlisted. */
  readonly placeCount: number;
  readonly shortlistedCount: number;
  /** The place chosen as primary, when one is and the reader may see places. */
  readonly primaryLocationId: number | null;
}

/**
 * Everything a conversation may point at in one production. Each half is null
 * when the reader may not see that feature (`READ_LOCATIONS`,
 * `READ_REQUIREMENTS`), and a token they cannot resolve reads as a plain
 * "a place" / "a script location".
 */
export interface ConversationLocations {
  readonly places: readonly ConversationPlace[] | null;
  readonly scriptLocations: readonly ConversationScriptLocation[] | null;
}

/** The Conversations tab of a project, and one thread in it — the SPA's addresses,
 *  which the digest email links to. A project id is accepted by `parseProjectAddress`. */
export function conversationsPath(projectId: string): string {
  return `/projects/${projectId}/conversations`;
}

export function conversationPath(projectId: string, threadId: string): string {
  return `${conversationsPath(projectId)}/${threadId}`;
}
